import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LG_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { NeuNeuAvatarLayer } from './NeuNeuAvatar';
import { NeuNeuPanel } from './NeuNeuPanel';
import { NEUNEU_TRANSITION_MS } from './neuNeuConstants';

type Rect = { left: number; top: number; width: number; height: number };

function measure(el: HTMLElement | null): Rect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

interface NeuNeuWidgetProps {
  /** `sidebar` = docked at bottom of nav; `fab` = floating bottom-right (mobile). */
  placement?: 'sidebar' | 'fab';
}

export function NeuNeuWidget({ placement = 'fab' }: NeuNeuWidgetProps) {
  const { t } = useT();
  const isLgUp = useMediaQuery(LG_MEDIA_QUERY);
  const [open, setOpen] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [layerRect, setLayerRect] = useState<Rect | null>(null);

  const fabRef = useRef<HTMLButtonElement>(null);
  const inSidebar = placement === 'sidebar';

  // Avoid duplicate widgets: sidebar only on desktop, fab only below lg
  const hidden = inSidebar ? !isLgUp : isLgUp;

  const updatePosition = useCallback(() => {
    setLayerRect(measure(fabRef.current));
  }, []);

  useLayoutEffect(() => {
    if (hidden) {
      setLayerRect(null);
      return;
    }
    updatePosition();
  }, [updatePosition, hidden, open]);

  useEffect(() => {
    if (hidden) return;
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [updatePosition, hidden]);

  useEffect(() => {
    if (open) {
      setShowPanel(true);
      return;
    }
    const tId = window.setTimeout(() => setShowPanel(false), NEUNEU_TRANSITION_MS);
    return () => window.clearTimeout(tId);
  }, [open]);

  // Close panel when switching breakpoints (widget instance swaps)
  useEffect(() => {
    if (hidden && open) setOpen(false);
  }, [hidden, open]);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setShowPanel(true);
    requestAnimationFrame(() => setOpen(true));
  };

  if (hidden) return null;

  return (
    <>
      {/* 3D avatar — follows the hit target */}
      {layerRect && (
        <div
          aria-hidden
          className={cn('fixed z-55 pointer-events-none neuneu-float')}
          style={{
            left: layerRect.left,
            top: layerRect.top,
            width: layerRect.width,
            height: layerRect.height,
            filter: 'brightness(1.15) saturate(1.8)',
          }}
        >
          <NeuNeuAvatarLayer className="h-full w-full" />
        </div>
      )}

      {/* Hit target */}
      <button
        ref={fabRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={open ? t('neuneu.close') : t('neuneu.open')}
        className={cn(
          'cursor-pointer rounded-full bg-transparent shadow-none',
          'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-background',
          inSidebar
            ? 'relative z-60 mx-auto h-20 w-20 shrink-0'
            : 'fixed bottom-6 right-4 z-60 h-28 w-28 sm:right-6 sm:h-32 sm:w-32',
        )}
      />

      {showPanel && (
        <NeuNeuPanel
          open={open}
          onClose={() => setOpen(false)}
          placement={inSidebar ? 'sidebar' : 'fab'}
        />
      )}
    </>
  );
}
